import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { Input } from '../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../../components/ui/select';
import SlotDisplayCondition from '../../../../types/BotBuilder/SlotDisplayCondition';

const VARIABLE_PATTERN =
  /\$\{intentContext\.slots\['([^']+)'\]\}|\$\{chatbotContext\.system\.([^}\s]+)\}|\$\{sessionContext\.global\.([^}\s]+)\}/g;

const DISPLAY_BEHAVIOR_HIDE_LINE = 'HIDE_LINE';
const DISPLAY_BEHAVIOR_SHOW_FALLBACK = 'SHOW_FALLBACK';

interface VariableReference {
  placeholder: string;
  displayName: string;
}

interface SlotDisplayConditionListProps {
  content: string;
  slotDisplayConditions: SlotDisplayCondition[];
  onChange: (conditions: SlotDisplayCondition[]) => void;
  disabled?: boolean;
}

function extractVariableReferences(content: string): VariableReference[] {
  const pattern = new RegExp(VARIABLE_PATTERN.source, 'g');
  const seen = new Set<string>();
  const refs: VariableReference[] = [];

  Array.from(content.matchAll(pattern)).forEach((match) => {
    const placeholder = match[0];
    if (seen.has(placeholder)) return;
    seen.add(placeholder);

    const displayName = match[1] ?? match[2] ?? match[3];
    refs.push({ placeholder, displayName });
  });

  return refs;
}

function buildDefaultCondition(slotName: string): SlotDisplayCondition {
  return {
    slotName,
    conditional: false,
    displayBehavior: DISPLAY_BEHAVIOR_HIDE_LINE,
    fallbackText: null
  };
}

function SlotDisplayConditionList({
  content,
  slotDisplayConditions,
  onChange,
  disabled
}: Readonly<SlotDisplayConditionListProps>): React.ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);

  const variableRefs = useMemo(
    () => extractVariableReferences(content),
    [content]
  );

  const displayNameByPlaceholder = useMemo(
    () =>
      new Map(variableRefs.map((ref) => [ref.placeholder, ref.displayName])),
    [variableRefs]
  );

  useEffect(() => {
    if (variableRefs.length === 0) {
      if (slotDisplayConditions.length > 0) {
        onChange([]);
      }
      return;
    }

    const existingByName = new Map(
      slotDisplayConditions.map((c) => [c.slotName, c])
    );

    const referencedSet = new Set(variableRefs.map((ref) => ref.placeholder));
    const currentSet = new Set(slotDisplayConditions.map((c) => c.slotName));

    const hasNewSlots = variableRefs.some(
      (ref) => !currentSet.has(ref.placeholder)
    );
    const hasRemovedSlots = slotDisplayConditions.some(
      (c) => !referencedSet.has(c.slotName)
    );

    if (!hasNewSlots && !hasRemovedSlots) return;

    const synced = variableRefs.map(
      (ref) =>
        existingByName.get(ref.placeholder) ??
        buildDefaultCondition(ref.placeholder)
    );
    onChange(synced);
  }, [variableRefs, slotDisplayConditions, onChange]);

  const handleConditionalToggle = useCallback(
    (index: number, checked: boolean) => {
      const updated = [...slotDisplayConditions];
      updated[index] = { ...updated[index], conditional: checked };
      onChange(updated);
    },
    [slotDisplayConditions, onChange]
  );

  const handleDisplayBehaviorChange = useCallback(
    (index: number, value: string) => {
      const updated = [...slotDisplayConditions];
      const behavior = value as SlotDisplayCondition['displayBehavior'];
      updated[index] = {
        ...updated[index],
        displayBehavior: behavior,
        fallbackText:
          behavior === DISPLAY_BEHAVIOR_HIDE_LINE
            ? null
            : (updated[index].fallbackText ?? '')
      };
      onChange(updated);
    },
    [slotDisplayConditions, onChange]
  );

  const handleFallbackTextChange = useCallback(
    (index: number, value: string) => {
      const updated = [...slotDisplayConditions];
      updated[index] = { ...updated[index], fallbackText: value };
      onChange(updated);
    },
    [slotDisplayConditions, onChange]
  );

  return (
    <div className='flex flex-col gap-2 overflow-hidden'>
      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => setIsExpanded(!isExpanded)}
          className='text-gray-500 hover:text-gray-700 p-0.5 -ml-1'
          disabled={disabled}
        >
          {isExpanded ? (
            <ChevronDown className='h-4 w-4' />
          ) : (
            <ChevronRight className='h-4 w-4' />
          )}
        </button>
        <Label className='text-sm font-medium'>Variable display settings</Label>
      </div>

      {isExpanded && variableRefs.length === 0 && (
        <p className='text-sm text-muted-foreground pl-5'>
          No variables referenced in content
        </p>
      )}

      {isExpanded &&
        slotDisplayConditions.map((condition, index) => (
          <div
            key={condition.slotName}
            className='flex flex-col gap-2 p-3 border rounded-lg bg-white'
          >
            <Label className='text-sm text-muted-foreground'>
              {displayNameByPlaceholder.get(condition.slotName) ??
                condition.slotName}
            </Label>

            <div className='flex items-center gap-2'>
              <Switch
                checked={condition.conditional}
                onCheckedChange={(checked: boolean) =>
                  handleConditionalToggle(index, checked)
                }
                disabled={disabled}
              />
              <span className='text-sm'>Conditional</span>
            </div>

            {condition.conditional && (
              <div className='flex flex-col gap-2 pl-2 border-l-2 border-gray-200'>
                <Select
                  value={condition.displayBehavior}
                  onValueChange={(value: string) =>
                    handleDisplayBehaviorChange(index, value)
                  }
                  disabled={disabled}
                >
                  <SelectTrigger className='bg-gray-50'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DISPLAY_BEHAVIOR_HIDE_LINE}>
                      Hide line
                    </SelectItem>
                    <SelectItem value={DISPLAY_BEHAVIOR_SHOW_FALLBACK}>
                      Show fallback
                    </SelectItem>
                  </SelectContent>
                </Select>

                {condition.displayBehavior ===
                  DISPLAY_BEHAVIOR_SHOW_FALLBACK && (
                  <Input
                    value={condition.fallbackText ?? ''}
                    onChange={(e) =>
                      handleFallbackTextChange(index, e.target.value)
                    }
                    placeholder='Fallback message'
                    className='bg-gray-50'
                    disabled={disabled}
                  />
                )}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

export default SlotDisplayConditionList;
